import {IBlueRibbonEventsHandler} from './blue_ribbon_event_handler_interface';
import {
    OptEvent,
    GameDetailsChangedEvent,
    GameStatusChangedEvent,
    PotStateChangedEvent,
    WinEvent,
    ComplexMatchDetails,
    GameObject
} from '../blue_ribbon_api_interfaces';
import Event from '../../../utils/event';
import { SYSTEM_EVENTS } from "../../../events/events";
import BaseObject from "../../../base/base_object";
import { BlueRibbonPartyJackpot } from "../jackpots/blue_ribbon_party_jackpot";
import {BlueRibbonExecutionMonitor, convertToCents} from '../blue_ribbon_helpers';
import ServiceFactory from '../../service_factory';
import {IBlueRibbonConnection} from '../blue_ribbon_connection_interface';
import { BLUE_RIBBON_POT_STATE, PartyJackpotType } from '../../../../../common/interfaces/blue-ribbon-party-jackpot.interface';

const MUST_JP_TYPE_CUSTOM_OBJECT_KEY        = "JP_TYPE";
const MUST_DROP_HOUR_CUSTOM_OBJECT_KEY      = "DROP_HOUR";
const START_HOUR_CUSTOM_OBJECT_KEY          = "START_HOUR";
const END_HOUR_CUSTOM_OBJECT_KEY            = "END_HOUR";
const B2C_LINK_CUSTOM_OBJECT_KEY            = "B2C_LINK";
const HAS_REAL_MONEY_WIN_PRIZE_CUSTOM_OBJECT_KEY = "HAS_REAL_MONEY_WIN_PRIZE";
const LIVE_CASINO_CUSTOM_OBJECT_KEY         = "LIVE_CASINO";

export default class BlueRibbonPartyJackpotEventsHandler extends BaseObject implements IBlueRibbonEventsHandler {
    static CLASS = "BlueRibbonPartyJackpotEventsHandler";

    private _partyJackpotsMap: Record<string, BlueRibbonPartyJackpot> = {};

    constructor(private _serviceFactory: ServiceFactory,
                private _blueRibbonConnection: IBlueRibbonConnection,
                private _getPersonalJackpotId: () => string) {
        super();
        this._executionMonitor = new BlueRibbonExecutionMonitor(this._serviceFactory);
    }

    private _executionMonitor: BlueRibbonExecutionMonitor;
    
    dispose() {

    }

    onConnected(matchedOperatorGames: ComplexMatchDetails[]) {
        this._loadAllPartyJackpots(matchedOperatorGames);
    }

    onGamesFeedStarted(data: GameObject[]): void {
        if (!this._serviceFactory.oAuth2Service().isAuthorized()) {
            return;
        }

        for(let jackpotId in this._partyJackpotsMap) {
            const partyJackpot = this._partyJackpotsMap[jackpotId];
            if(partyJackpot.isActive) {
                this._blueRibbonConnection.isPlayerOptInToJackpotGame({
                    gameId: jackpotId
                }).then(data => {
                    partyJackpot.setIsOptedIn(data.playerOptState.isOptIn);
                });
            }
        }
    }

    getAllPartyJackpots(): BlueRibbonPartyJackpot[] {
        return Object.values(this._partyJackpotsMap);
    }

    public onOptIn(data: OptEvent): void {
        const partyJackpot = this._partyJackpotsMap[data.playerOptState.gameId];
        if (!partyJackpot) {
            return;
        }

        partyJackpot.setIsOptedIn(true);
        this._fireStateChangedEvent(partyJackpot);
    }

    public onOptOut(data: OptEvent): void {
        const partyJackpot = this._partyJackpotsMap[data.playerOptState.gameId];
        if (!partyJackpot) {
            return;
        }

        partyJackpot.setIsOptedIn(false);
        this._serviceFactory.blueRibbonService().makePartyJackpotStatusNeutral(partyJackpot.jackpotId);
        this._fireStateChangedEvent(partyJackpot);
    }

    public onGameDetailsChanged(data: GameDetailsChangedEvent): void {
        const partyJackpot = this._partyJackpotsMap[data.gameDetails.gameId];
        if (!partyJackpot) {
            return;
        }

        partyJackpot.setName(data.gameDetails.name);
        if (data.gameDetails.pots?.length >= 1) {
            const value = convertToCents(data.gameDetails.pots[0].seedPotAmount);
            partyJackpot.setInitialValue(value);
            if (partyJackpot.currentValue === 0) {
                partyJackpot.setCurrentValue(value);
            }
        }

        partyJackpot.setType(data.gameDetails.customJackpotParams[MUST_JP_TYPE_CUSTOM_OBJECT_KEY]);
        if (partyJackpot.type === PartyJackpotType.Daily) {
            partyJackpot.setMustDropHour(data.gameDetails.customJackpotParams[MUST_DROP_HOUR_CUSTOM_OBJECT_KEY]);
        } else if (partyJackpot.type === PartyJackpotType.Hourly) {
            partyJackpot.setStartHour(data.gameDetails.customJackpotParams[START_HOUR_CUSTOM_OBJECT_KEY]);
            partyJackpot.setEndHour(data.gameDetails.customJackpotParams[END_HOUR_CUSTOM_OBJECT_KEY]);
        }
        partyJackpot.setB2CLink(data.gameDetails.customJackpotParams[B2C_LINK_CUSTOM_OBJECT_KEY]);
        partyJackpot.setHasRealMoneyWinPrize(data.gameDetails.customJackpotParams[HAS_REAL_MONEY_WIN_PRIZE_CUSTOM_OBJECT_KEY]);
        partyJackpot.setLiveCasino(data.gameDetails.customJackpotParams[LIVE_CASINO_CUSTOM_OBJECT_KEY]);

        this._fireStateChangedEvent(partyJackpot);
    }

    public onGameStatusChanged(data: GameStatusChangedEvent): void {
        const partyJackpot = this._partyJackpotsMap[data.gameStatus.gameId];
        if (!partyJackpot) {
            return;
        }

        partyJackpot.setIsActive(data.gameStatus.status === 'ACTIVE', data.gameStatus.inactiveReason);
        this._fireStateChangedEvent(partyJackpot);
    }

    public onPotStateChanged(data: PotStateChangedEvent): void {
        const partyJackpot = this._partyJackpotsMap[data.jackpotPotState.gameId];
        if (!partyJackpot) {
            return;
        }

        partyJackpot.setPotState(data.jackpotPotState.potStatus as BLUE_RIBBON_POT_STATE);
        partyJackpot.setCurrentValue(convertToCents(data.jackpotPotState.progressive));
        partyJackpot.setLastWin(data.jackpotPotState.lastWinTs);
        this._fireStateChangedEvent(partyJackpot);
    }

    public onWin(data: WinEvent): void {
        const partyJackpot = this._partyJackpotsMap[data.winningDetails.gameId];
        if (!partyJackpot) {
            return;
        }

        partyJackpot.setWinAmount(data.winningDetails.currency,
                                  convertToCents( data.winningDetails.amount),
                                  data.winningDetails.playerId);
        this._fireStateChangedEvent(partyJackpot);
        Event.fire(SYSTEM_EVENTS.BLUE_RIBBON_PARTY_JACKPOT_WIN, partyJackpot);
    }

    private _fireStateChangedEvent(jackpot: BlueRibbonPartyJackpot) {
        Event.fire(SYSTEM_EVENTS.BLUE_RIBBON_PARTY_JP_STATE_CHANGED, jackpot);
    }


    private _loadAllPartyJackpots(operatorGames: ComplexMatchDetails[]): void {
        const operatorGamesGroupedByJackpotId = this._groupGamesByPartyJackpotId(operatorGames);
        const partyJackpots: Record<string, BlueRibbonPartyJackpot> = {};
        for(let jackpotId in operatorGamesGroupedByJackpotId) {
            const gamesInJackpot = operatorGamesGroupedByJackpotId[jackpotId];
            partyJackpots[jackpotId] = new BlueRibbonPartyJackpot(this._serviceFactory, this.logger, jackpotId, gamesInJackpot.map(g => g.gameId))
        }

        this._partyJackpotsMap = partyJackpots;
        this._executionMonitor.info('Received party jackpot games:', partyJackpots);
    }

    private _groupGamesByPartyJackpotId(operatorGames: ComplexMatchDetails[]): Record<string, ComplexMatchDetails[]> {
        const operatorGamesGroupedByJackpotId: Record<string, ComplexMatchDetails[]> = {};
        operatorGames.forEach(gameInfo => {
            if (gameInfo.jackpotGameId === this._getPersonalJackpotId())
                return;

            if(!operatorGamesGroupedByJackpotId[gameInfo.jackpotGameId]) {
                operatorGamesGroupedByJackpotId[gameInfo.jackpotGameId] = [];
            }
            operatorGamesGroupedByJackpotId[gameInfo.jackpotGameId].push(gameInfo);

        });
        return operatorGamesGroupedByJackpotId
    }

}
